import { useState, useMemo, useCallback, useRef } from 'react';
import { createChatbotService, ChatMessage } from '../services/chatbot';
import { trackEvent } from '../lib/analytics';

export const useChatbot = () => {
    const chatbotService = useMemo(() => createChatbotService(), []);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [navigationPath, setNavigationPath] = useState<string | null>(null);
    const hasTrackedChatStart = useRef(false);

    const sendMessage = useCallback(async (newMessage: string) => {
        if (!hasTrackedChatStart.current) {
            trackEvent('chat_started');
            hasTrackedChatStart.current = true;
        }
        const userMessage: ChatMessage = { role: 'user', text: newMessage };
        setMessages(prevMessages => [...prevMessages, userMessage]);
        setIsLoading(true);
        setError(null);
        setNavigationPath(null);

        const currentHistory = [...messages, userMessage];
        const response = await chatbotService.getResponse(currentHistory, newMessage);

        if (response.success) {
            if (response.text) {
                const modelMessage: ChatMessage = { role: 'model', text: response.text };
                setMessages(prevMessages => [...prevMessages, modelMessage]);
            }
            if (response.navigation) setNavigationPath(response.navigation);
        } else {
            const errorMessage = response.error || 'An unknown error occurred.';
            setError(errorMessage);
            setMessages(prevMessages => [...prevMessages, { role: 'model', text: `Sorry, I encountered an error: ${errorMessage}` }]);
        }
        setIsLoading(false);
    }, [chatbotService, messages]);

    const clearNavigation = useCallback(() => setNavigationPath(null), []);

    return { messages, isLoading, error, sendMessage, navigationPath, clearNavigation };
};
